import config, {Brand} from '../../config';
import {universal} from '../../design-system/tokens';
import theme from '../../style/theme';

/**
 * Per-brand footer chrome for the Header/Footer redesign (Figma board 470:20041), keyed on
 * `config.BASE_TENANT`.
 *
 * Colours come from the brand theme (assets/common/theme/{brand}/theme.js):
 * - ground: `colour.background.general.primary`. It equals the Figma brand variable on BM, SNS
 *   and HB. TC keeps the theme #A0CE66 over Figma "Thirsty Camel/Green" #ACCA57 (decided
 *   2026-10-19, so the footer matches the rest of the TC site).
 * - divider, legalText: `colour.footer.*`. Figma draws the BM navy divider on every brand, a
 *   design error; the per-brand values and their SHORTCUTs live in each theme.js.
 * Text on the ground and the legal bar are Figma `Universal/*` variables (`universal`).
 */
export type BrandChrome = {
  /**
   * Brand name for footer copy. Not `config.APP_NAME`: the dev and beta env files prefix it
   * with "DEV " / "BETA "; the prod values (.env.{brand}.prod.yml) equal these names.
   */
  name: string;
  /** Footer ground. */
  ground: string;
  /** Text and icons on the ground. */
  onGround: string;
  /** Footer band dividers. */
  divider: string;
  legalText: string;
  /** Store badge artwork, App Store and Google Play: black on a light ground, white on a dark one. */
  storeBadge: 'dark' | 'light';
};

type BrandOnly = Pick<BrandChrome, 'name' | 'onGround' | 'storeBadge'>;

const chromeByBrand: Record<Exclude<Brand, Brand.DEVELOPMENT>, BrandOnly> = {
  [Brand.BOTTLEMART]: {name: 'Bottlemart', onGround: universal.white, storeBadge: 'dark'},
  [Brand.THIRSTY_CAMEL]: {name: 'Thirsty Camel', onGround: universal.black, storeBadge: 'dark'},
  [Brand.HARRY_BROWN]: {name: 'Harry Brown', onGround: universal.white, storeBadge: 'light'},
  [Brand.SIPNSAVE]: {name: 'SipnSave', onGround: universal.black, storeBadge: 'dark'},
};

export const brandChrome: BrandChrome = {
  ...(chromeByBrand[config.BASE_TENANT as keyof typeof chromeByBrand] ??
    chromeByBrand[Brand.BOTTLEMART]),
  ground: theme.colour.background.general.primary,
  divider: theme.colour.footer.divider,
  legalText: theme.colour.footer.legalText,
};
